import React, { useEffect, useRef, useState } from 'react';
import {
  guidelineTypeOptions,
  initialGuidelineKanbanState,
  initialGuidelineClientState,
  initialGuidelineReviewerState,
  kanbanGuidelineStepNumber,
} from 'constants/index';
import { getDatabase, ref, onValue, off, update } from 'firebase/database';
import { useAuth } from 'hooks/useAuth';
import { MetaTags } from 'react-meta-tags';
import { Container, CardTitle, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Board, { moveCard } from '@asseinfo/react-kanban';

import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { processGuidelines } from 'utils/processGuidelines';
import KanbanCardGuideline from './components/Kanban-Card-Guideline';
import ChangelogModal from '../../components/Changelog-Modal';
import KanbanHeader from '../../components/Kanban-Header';
import HeaderContext from '../../components/Tasks-Filter/TasksFilter.Context';
import Breadcrumbs from '../../components/Breadcrumb';
import { KanbanGuidelineLayout } from './styles';
import ModalArchive from './components/Modal-Archive';

const KanbanGuideline = () => {
  const firebaseHelper = getFirebaseBackend();

  const { t: translate } = useTranslation();
  const { user } = useAuth();

  const accountId = user?.account;
  const userStatus = user?.adminStatus;
  const initialBoard =
    userStatus === 'Client' || userStatus === 'Client Admin'
      ? initialGuidelineClientState
      : userStatus === 'Reviewer'
        ? initialGuidelineReviewerState
        : initialGuidelineKanbanState;
  const containerRef = useRef();

  const [board, setBoard] = useState({ columns: initialBoard });
  const [filter, setFilter] = useState(null);
  const [filterTotal, setFilterTotal] = useState(0);
  const [draggingFromColumn, setDraggingFromColumn] = useState('');
  const [modalArchive, setModalArchive] = useState(false);
  const [guidelineDataClicked, setGuidelineDataClicked] = useState([]);

  const getGuidelineType = (type) => {
    for (const option of guidelineTypeOptions) {
      if (option.value === type) {
        return option.label;
      }
    }
  };

  const getBoardIndex = (index) => {
    return board.columns.findIndex(
      (column) => column.title === initialGuidelineKanbanState[index].title,
    );
  };

  const stringPerColumn = {
    Backlog: () => {
      return `(${board.columns[getBoardIndex(0)].cards.length})`;
    },
    'Guideline approval': () => {
      return `(${board.columns[getBoardIndex(1)].cards.length})`;
    },
    'Content Structure': () => {
      return `(${board.columns[getBoardIndex(2)].cards.length})`;
    },
    'Pre review': () => {
      return `(${board.columns[getBoardIndex(3)].cards.length})`;
    },
    'Content review': () => {
      return `(${board.columns[getBoardIndex(4)].cards.length})`;
    },
    'Internal approval': () => {
      return `(${board.columns[getBoardIndex(5)].cards.length})`;
    },
    'Content approval': () => {
      return `(${board.columns[getBoardIndex(6)].cards.length})`;
    },
    Approved: () => {
      return `(${board.columns[getBoardIndex(7)].cards.length})`;
    },
    Published: () => {
      return `(${board.columns[getBoardIndex(8)].cards.length})`;
    },
  };

  const tooltipText = {
    Backlog: translate('Planned content for production.'),
    'Guideline approval': translate('Approved content scheduled for production.'),
    'Content Structure': translate('Stage for structuring topics related to secondary keywords.'),
    'Pre review': translate('Content production stage in topics.'),
    'Content review': translate(
      'Stage for creating the doc, writing the full content, and first review.',
    ),
    'Internal approval': translate(
      'Final stage of content and SEO review, with the creation of the final document for delivery.',
    ),
    'Content approval': translate(
      'Final document is created in the client folder on Drive and has been sent for approval.',
    ),
    Approved: translate('Approved content with no further adjustments.'),
    Published: translate('Published content.'),
  };

  const onCardDragEnd = (card, source, destination) => {
    setDraggingFromColumn('');

    if (source.fromColumnId === destination.toColumnId) {
      const updatedBoard = moveCard(board, source, destination);
      const updates = {};

      // Updates the position field on each card
      updatedBoard.columns.forEach((column) => {
        column.cards.forEach((card, indexCard) => {
          card.position = indexCard;
        });
      });
      // If the column was changed updates the column on the moved card
      if (card.column !== kanbanGuidelineStepNumber[destination.toColumnId]) {
        card.column = kanbanGuidelineStepNumber[destination.toColumnId];
      }
      // Sends an update request to change the position for every card,
      // firebase only updates the needed ones
      updatedBoard.columns.forEach((column) => {
        column.cards.forEach((card) => {
          updates[`guidelines/${accountId}/${card.id}`] = { ...card };
        });
      });

      const db = getDatabase();
      update(ref(db), updates).catch((error) => {
        console.error('Erro ao atualizar os cartões:', error);
      });

      setBoard(updatedBoard);
    }
  };

  const toggleModalArchive = () => setModalArchive(!modalArchive);

  useEffect(() => {
    const columns = document.querySelectorAll(
      `.react-kanban-column:not([data-rbd-draggable-id="column-draggable-${draggingFromColumn}"])`,
    );

    if (draggingFromColumn !== '') {
      columns.forEach((column) => {
        column.classList.add('no-drag');
      });
    } else {
      columns.forEach((column) => {
        column.classList.remove('no-drag');
      });
    }
  }, [draggingFromColumn]);

  useEffect(() => {
    if (!accountId) return;

    const db = getDatabase();
    const guidelinesRef = ref(db, `guidelines/${accountId}`);

    const handleValueChange = async (snapshot) => {
      const data = snapshot.val();

      const guidelinesWithIds = Object.entries(data ?? {}).map(([key, value]) => ({
        ...value,
        id: key,
      }));

      const filteredGuidelines = guidelinesWithIds.filter((guideline) => {
        const isClientAccessible =
          guideline.column === 'GUIDELINE_APPROVAL' ||
          guideline.column === 'CONTENT_APPROVAL' ||
          guideline.column === 'APPROVED' ||
          guideline.column === 'PUBLISHED';

        if (userStatus === 'Client' || userStatus === 'Client Admin') return isClientAccessible;
        return true;
      });

      const nonArchivedGuidelines = Object.values(filteredGuidelines).filter(
        (guideline) => !guideline.archived,
      );

      const processedGuidelines = await processGuidelines({
        receivedGuidelines: nonArchivedGuidelines,
        initialBoard,
        filter,
        setFilterTotal,
        getGuidelineType,
      });

      const highestPositionCardsByColumn = processedGuidelines.map((column) => {
        const columnLength = column.cards.length + 1;

        return {
          columnName: column.title
            .toUpperCase()
            .replace(/[^A-Z0-9 ]/g, '')
            .replace(/\s+/g, '_'),
          lastPosition: columnLength > 0 ? columnLength - 1 : 0,
        };
      });

      if (processGuidelines.length > 0) {
        highestPositionCardsByColumn.forEach((column) => {
          firebaseHelper.saveGuidelineMetadataLastPosition(
            accountId,
            column.columnName,
            column.lastPosition,
          );
        });
      }
      setBoard({ columns: processedGuidelines });
    };

    onValue(guidelinesRef, handleValueChange);

    return () => {
      off(guidelinesRef, 'value', handleValueChange);
    };
  }, [accountId, filter]);

  return (
    <React.Fragment>
      <KanbanGuidelineLayout>
        <div className="page-content">
          <MetaTags>
            <title>{`${translate('Guidelines')} | Ectools`}</title>
          </MetaTags>
          <Container fluid>
            <Breadcrumbs title={translate('Content')} breadcrumbItem={translate('Guidelines')} />

            <ChangelogModal />

            <HeaderContext.Provider value={{ setFilter }}>
              <KanbanHeader
                board={board}
                accountId={accountId}
                filterTotal={filterTotal}
                userStatus={userStatus}
                isGuideline
              />
            </HeaderContext.Provider>

            <div ref={containerRef} style={{ height: '64.9vh' }}>
              {userStatus !== 'Reviewer' && (
                <ModalArchive
                  modalFunction={toggleModalArchive}
                  modalState={modalArchive}
                  dataIndividual={guidelineDataClicked}
                />
              )}

              <Board
                disableColumnDrag
                renderColumnHeader={({ title }) => (
                  <div
                    className="d-flex justify-content-between align-items-center px-2"
                    style={{ height: '3.25rem' }}
                  >
                    <CardTitle className="p-0">
                      {translate(title) + stringPerColumn[title]()}
                    </CardTitle>
                    <i
                      className="bx bx-info-circle"
                      id={title.replace(' ', '-')}
                      style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                    />
                    <UncontrolledTooltip placement="top" target={title.replace(' ', '-')}>
                      {tooltipText[title]}
                    </UncontrolledTooltip>
                  </div>
                )}
                renderCard={(data, { dragging }) => (
                  <KanbanCardGuideline
                    dragging={dragging}
                    data={data}
                    setDraggingFromColumn={setDraggingFromColumn}
                    draggingFromColumn={draggingFromColumn}
                    key={data.id}
                    setGuidelineDataClicked={setGuidelineDataClicked}
                    toggleModalArchive={toggleModalArchive}
                  >
                    {data}
                  </KanbanCardGuideline>
                )}
                onCardDragEnd={onCardDragEnd}
              >
                {board}
              </Board>
            </div>
          </Container>
        </div>
      </KanbanGuidelineLayout>
    </React.Fragment>
  );
};

export default KanbanGuideline;
